import React from 'react';
import './SyntaxHelpPopover.css';

const MOVE_EXAMPLES = [
  { query: 'move:earthquake',          label: 'Has Earthquake' },
  { query: 'move:fake-out',            label: 'Has Fake Out (hyphen)' },
  { query: 'move:"fake out"',          label: 'Has Fake Out (quoted)' },
  { query: 'move:tailwind type:flying', label: 'Flying Tailwind setters' },
];

const EXAMPLES = [
  { query: 'charizard',             label: 'Name search' },
  { query: 'type:fire',             label: 'By type' },
  { query: 'type:fire type:flying', label: 'Dual type' },
  { query: 'type:dragon bst>=600',  label: 'High-BST Dragons' },
  { query: 'gen:1 is:legendary',    label: 'Gen 1 legendaries' },
  { query: 'atk>=130 spe>=100',     label: 'Fast hard hitters' },
  { query: 'ability:levitate -type:ghost', label: 'Levitate non-Ghosts' },
  { query: 'is:pseudo',             label: 'Pseudo-legendaries' },
  { query: 'is:starter',            label: 'Starter Pokémon' },
  { query: 'gen:5 bst>=580 -is:legendary', label: 'Gen 5 powerhouses' },
  { query: 'sort:speed type:electric', label: 'Fastest Electric types' },
  { query: 'weight<5',              label: 'Ultra-lightweight (kg)' },
  { query: '(type:fire or type:water)', label: 'Fire OR Water' },
  { query: '(is:legendary or is:mythical)', label: 'Legendary or Mythical' },
  { query: '(type:fire or type:water) bst>=500', label: 'Fire/Water powerhouses' },
  { query: '-(type:normal or type:flying)', label: 'Not Normal or Flying' },
];

const OR_EXAMPLES = [
  { query: '(type:fire or type:water)',           label: 'Fire or Water type' },
  { query: '(is:legendary or is:mythical)',        label: 'Legendary or Mythical' },
  { query: '(type:fire or type:water) bst>=500',  label: 'Combined with other filters' },
  { query: '-(type:normal or type:flying)',        label: 'Negate the whole group' },
];

const OPERATORS = [
  { op: 'type:',    alias: 't:',  desc: 'Type',                                          example: 'type:water' },
  { op: 'name:',    alias: 'n:',  desc: 'Name contains',                                 example: 'name:saur' },
  { op: 'gen:',     alias: 'g:',  desc: 'Generation (1–9)',                              example: 'gen:3' },
  { op: 'bst',      alias: null,  desc: 'Base Stat Total',                               example: 'bst>=600' },
  { op: 'hp / atk / def',  alias: null, desc: 'Individual stats',                        example: 'spe>100' },
  { op: 'spa / spd / spe', alias: null, desc: '(Sp.Atk / Sp.Def / Speed)',               example: 'spa>=130' },
  { op: 'ability:', alias: 'a:',  desc: 'Has ability',                                   example: 'ability:levitate' },
  { op: 'move:',    alias: 'm:',  desc: 'Learns move in SV — use quotes or hyphens for multi-word', example: 'move:fake-out' },
  { op: 'weight',   alias: 'w:',  desc: 'Weight in kg',                                  example: 'weight<10' },
  { op: 'is:',      alias: null,  desc: 'legendary · mythical · pseudo · baby · starter · mono · dual · mega · gmax · restricted', example: 'is:mega' },
  { op: 'in:',      alias: null,  desc: 'Game availability',                             example: 'in:champ' },
  { op: 'sort:',    alias: 'o:',  desc: 'Sort highest first (append -asc for lowest)',   example: 'sort:bst' },
  { op: 'sortlow:', alias: null,  desc: 'Sort lowest first',                             example: 'sortlow:spe' },
  { op: 'sorthigh:', alias: null, desc: 'Sort highest first (explicit)',                 example: 'sorthigh:atk' },
];

function SyntaxHelpPopover({ onClose, onInsert }) {
  return (
    <div className="shp-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="shp-panel">
        <div className="shp-header">
          <h2>Search Syntax</h2>
          <button className="shp-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <p className="shp-intro">
          Combine filters in one box separated by spaces. All filters must match (AND logic).
          Prefix with <code>-</code> to negate — e.g. <code>-type:normal</code>.
        </p>

        {/* ── Operators table ── */}
        <section>
          <h3>Operators</h3>
          <table className="shp-table">
            <thead>
              <tr><th>Operator</th><th>Alias</th><th>Meaning</th></tr>
            </thead>
            <tbody>
              {OPERATORS.map(({ op, alias, desc, example }) => (
                <tr key={op}>
                  <td><code>{op}</code></td>
                  <td>{alias ? <code>{alias}</code> : <span className="shp-none">—</span>}</td>
                  <td>
                    {desc}
                    {' '}
                    <button
                      className="shp-insert-btn"
                      onClick={() => onInsert(example)}
                      title={`Insert "${example}"`}
                    >
                      {example}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* ── Comparison operators note ── */}
        <p className="shp-note">
          Numeric operators: <code>:</code> (equals) <code>&gt;</code> <code>&gt;=</code> <code>&lt;</code> <code>&lt;=</code>
        </p>

        {/* ── Move filter ── */}
        <section>
          <h3>Move Filter</h3>
          <p className="shp-intro">
            Use <code>move:</code> (alias <code>m:</code>) to find Pokémon that learn a move in Scarlet/Violet.
            Hyphenate or quote multi-word moves — both work.
          </p>
          <div className="shp-examples">
            {MOVE_EXAMPLES.map(({ query, label }) => (
              <button key={query} className="shp-example" onClick={() => onInsert(query)} title="Click to use this query">
                <span className="shp-example-label">{label}</span>
                <code className="shp-example-query">{query}</code>
              </button>
            ))}
          </div>
        </section>

        {/* ── OR groups ── */}
        <section>
          <h3>OR Groups</h3>
          <p className="shp-intro">
            Wrap filters in <code>( )</code> separated by <code>or</code> to match any one of them.
            Prefix the whole group with <code>-</code> to negate it.
          </p>
          <div className="shp-examples">
            {OR_EXAMPLES.map(({ query, label }) => (
              <button
                key={query}
                className="shp-example"
                onClick={() => onInsert(query)}
                title="Click to use this query"
              >
                <span className="shp-example-label">{label}</span>
                <code className="shp-example-query">{query}</code>
              </button>
            ))}
          </div>
        </section>

        {/* ── Example queries ── */}
        <section>
          <h3>Example Queries</h3>
          <div className="shp-examples">
            {EXAMPLES.map(({ query, label }) => (
              <button
                key={query}
                className="shp-example"
                onClick={() => onInsert(query)}
                title="Click to use this query"
              >
                <span className="shp-example-label">{label}</span>
                <code className="shp-example-query">{query}</code>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default SyntaxHelpPopover;
